import type { ChatStore } from "../Store";

import { NotificationType } from "@/Modules/App/Models/Domain/NotificationType";

import { getSubscription } from "@/Services/TherapyApi/Subscriptions";
import { getAgreements } from "@/Services/TherapyApi/LegalAgreements";
import SubscriptionTransformer from "@/Modules/Transformers/Subscription";

import useUserStore from "@/Modules/User/Store";
import useChatStore from "@/Modules/Chat/Store";

import { hasValue } from "@/Utils/Objects";

async function loadChatRoomUserAH(chatRoomId: string): Promise<void> {
  const userStore = useUserStore();
  const chatStore = useChatStore();

  if (!hasValue(chatStore.rooms[chatRoomId])) return;

  chatStore.rooms[chatRoomId].user.isLoadingUser = true;

  const chatRoomUserId = chatStore.rooms[chatRoomId].user.id;
  const userId = userStore.user?.id;

  if (hasValue(userId)) {
    await Promise.all([
      loadSubscription(chatStore, chatRoomUserId, chatRoomId),
      loadLegalAgreements(chatStore, userStore, chatRoomUserId, chatRoomId),
    ]);
  }

  chatStore.rooms[chatRoomId].user.isLoadingUser = false;
}

async function loadSubscription(
  chatStore: ChatStore,
  chatRoomUserId: string,
  chatRoomId: string
): Promise<void> {
  try {
    const subscriptionApi = await getSubscription(chatRoomUserId);
    const subscription = SubscriptionTransformer.toDomain(subscriptionApi);

    if (subscription) {
      chatStore.rooms[chatRoomId]!.user.subscription = subscription;
    }
  } catch (error: unknown) {
    handleGenericError();
  }
}

async function loadLegalAgreements(
  chatStore: ChatStore,
  userStore: ReturnType<typeof useUserStore>,
  chatRoomUserId: string,
  chatRoomId: string
): Promise<void> {
  try {
    const userId = userStore.user ? userStore.user.id : "";
    const legalAgreementsApi = await getAgreements(chatRoomUserId, userId);
    const legalAgreements = legalAgreementsApi ? legalAgreementsApi : [];

    if (hasValue(legalAgreements)) {
      chatStore.rooms[chatRoomId]!.user.legalAgreements = legalAgreements;
    } else {
      chatStore.rooms[chatRoomId]!.user.legalAgreements = [];
    }
  } catch (error: unknown) {
    handleGenericError(userStore);
  }
}

function handleGenericError(userStore = useUserStore()): void {
  userStore.notification = {
    type: NotificationType.ERROR,
    message: "there_was_an_error_connecting_to_the_server_please_try_again_later",
    code: "generic error",
  };
}

export default loadChatRoomUserAH;
